<template>
  <div class="min-vh-100 bg-dark text-light">
    <!-- LOGIN BUTTON -->
    <div
      class="position-fixed top-0 end-0 p-3"
      style="z-index: 1050;"
    >
      <button
        @click="goLogin"
        class="btn btn-outline fw-bold px-4 py-2"
        style="color:#2bbdee; border-color:#2bbdee;"
      >
        Login
      </button>
    </div>

    <!-- HERO -->
    <section class="position-relative min-vh-100 d-flex align-items-center justify-content-center overflow-hidden">
      <div class="position-absolute top-0 start-0 w-100 h-100">
        <img
          :src="heroImage"
          alt="Luxury limousine service"
          class="w-100 h-100 object-fit-cover"
        />
        <div class="position-absolute top-0 start-0 w-100 h-100 bg-dark opacity-75"></div>
      </div>

      <div class="position-relative text-center container">
        <h1 class="display-3 fw-bold mb-4">
          From
          <span style="color:#2bbdee">sea </span>to<span style="color:#2bbdee"> shore</span>, <br />
          we've got every step covered
        </h1>

        <p class="lead mb-2">
          Book your ride — from flight updates to driver assignment.
        </p>

        <div class="d-flex justify-content-center align-items-center gap-3 mb-4">
          <img
            src="@/assets/award-100.jpg"
            alt="Entrepreneur Award"
            style="height: 120px; width: auto; opacity: 0.9;"
            class="footer-award"
          />

          <img
            src="@/assets/award-banner.jpg"
            alt="Entrepreneur Award Banner"
            style="height: 120px; width: auto; opacity: 0.9;"
            class="footer-award"
          />
        </div>

        <div class="d-flex flex-column flex-sm-row justify-content-center gap-3 mb-5">
          <button
            @click="openWhatsApp"
            class="btn btn-outline btn-lg fw-bold"
            style="color:#2bbdee; border-color:#2bbdee;"
          >
            Contact Us
          </button>
        </div>

        <div class="row row-cols-1 row-cols-md-3 g-4">
          <div class="col">
            <h3 class="fw-bold" style="color:#2bbdee">24/7</h3>
            <p>Available Service</p>
          </div>

          <div class="col">
            <h3 class="fw-bold" style="color:#2bbdee">10+ & Growing</h3>
            <p>Professional Drivers</p>
          </div>

          <div class="col">
            <h3 class="fw-bold" style="color:#2bbdee">99%</h3>
            <p>On-Time Performance</p>
          </div>
        </div>
      </div>
    </section>

    <!-- PAIN VS SOLUTION -->
    <section class="py-5 bg-dark">
      <div class="container">
        <h2 class="display-5 fw-bold text-center mb-5">
          From <span class="text-danger">Frustration</span> to
          <span style="color:#2bbdee">Perfection</span>
        </h2>

        <div class="row g-4">
          <div class="col-lg-6">
            <div class="card border-danger" style="background-color: rgba(139, 0, 0, 0.2);">
              <div class="card-body">
                <h3 class="h4 text-danger text-center mb-4 fw-bold">
                  Traditional Problems
                </h3>

                <ul class="list-unstyled mb-0">
                  <li
                    v-for="(p, i) in problems"
                    :key="'p' + i"
                    class="mb-3 d-flex align-items-start"
                  >
                    <X class="text-danger me-2" />
                    <span class="text-light">{{ p }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="col-lg-6">
            <div
              class="card"
              style="border: 2px solid #2bbdee; background-color: rgba(43, 189, 238, 0.1);"
            >
              <div class="card-body">
                <h3 class="h4 text-center mb-4 fw-bold" style="color: #2bbdee;">
                  Our Solutions
                </h3>

                <ul class="list-unstyled mb-0">
                  <li
                    v-for="(s, i) in solutions"
                    :key="'s' + i"
                    class="mb-3 d-flex align-items-start"
                  >
                    <Check style="color: #2bbdee;" />
                    <span class="ms-2" style="color: white;">{{ s }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- WHY CHOOSE -->
    <section class="py-5" style="background-color: rgba(255,255,255,0.05);">
      <div class="container">
        <h2 class="display-5 fw-bold text-center mb-3">
          Why Choose <span style="color:#2bbdee">AMD Transportation</span>?
        </h2>

        <p class="text-center text-muted mb-5">
          Experience the future of luxury transportation with our cutting-edge technology and personalized service.
        </p>

        <div class="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4 justify-content-center">
          <div
            v-for="(f, i) in features"
            :key="i"
            class="col"
          >
            <div class="card h-100 text-center bg-dark border-0 shadow-sm feature-card">
              <div class="card-body">
                <div class="d-flex justify-content-center mb-3">
                  <div
                    class="rounded-circle d-flex align-items-center justify-content-center"
                    style="width:70px; height:70px; background-color: rgba(43, 189, 238, 0.15);"
                  >
                    <component
                      :is="f.icon"
                      style="width:32px; height:32px; color:#2bbdee;"
                    />
                  </div>
                </div>

                <h3 class="h5 fw-bold feature-title">
                  {{ f.title }}
                </h3>

                <p
                  class="text-muted feature-desc"
                  v-html="f.description"
                ></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- BENEFITS -->
    <section class="py-5 bg-dark">
      <div class="container">
        <h2 class="display-5 fw-bold text-center mb-3">
          The <span style="color:#2bbdee">AMD Transportation</span> Advantage
        </h2>

        <p class="text-center text-muted mb-5">
          More than just transportation – it’s about peace of mind and exceptional service.
        </p>

        <div class="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4 justify-content-center">
          <div
            v-for="(b, i) in benefits"
            :key="i"
            class="col"
          >
            <div class="card h-100 text-center bg-dark border-0 shadow-sm">
              <div class="card-body">
                <div class="d-flex justify-content-center mb-3">
                  <div
                    class="rounded-circle d-flex align-items-center justify-content-center"
                    style="width:70px; height:70px; background-color: rgba(43, 189, 238, 0.15);"
                  >
                    <component
                      :is="b.icon"
                      style="width:32px; height:32px; color:#2bbdee;"
                    />
                  </div>
                </div>

                <h3 class="h5 fw-bold text-light">
                  {{ b.title }}
                </h3>

                <p class="text-muted">
                  {{ b.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="py-5" style="background-color: rgba(255,255,255,0.05);">
      <div class="container">
        <h2 class="display-5 fw-bold text-center mb-5">
          Frequently Asked <span style="color:#2bbdee">Questions</span>
        </h2>

        <div
          v-for="(faq, i) in faqs"
          :key="i"
          class="mb-3 p-3 rounded bg-dark text-light border border-secondary"
        >
          <div
            class="d-flex justify-content-between align-items-center fw-semibold"
            @click="toggleFAQ(i)"
            style="cursor: pointer;"
          >
            <span>{{ faq.question }}</span>
            <span class="fs-4" style="color:#2bbdee">
              {{ activeFAQ === i ? '−' : '+' }}
            </span>
          </div>

          <transition name="fade">
            <p
              v-show="activeFAQ === i"
              class="mt-3 text-white"
              v-html="faq.answer"
            ></p>
          </transition>
        </div>
      </div>
    </section>

    <!-- FAQ CHATBOT -->
    <div
      class="amd-chatbot position-fixed bottom-0 end-0 m-3"
      style="z-index: 2000;"
    >
      <button
        v-if="!chatOpen"
        @click="openChatbot"
        class="btn fw-bold shadow-lg px-4 py-3 rounded-pill"
        style="background:#2bbdee; color:white;"
      >
        Chat with us
      </button>

      <div
        v-else
        class="card bg-dark text-light border border-secondary shadow-lg chatbot-card"
      >
        <div class="d-flex justify-content-between align-items-center p-3 chatbot-header">
          <div>
            <div class="fw-bold" style="color:#2bbdee;">
              AMD Assistant
            </div>
            <small class="text-muted">
              Ask about booking, vehicles, port access, delays
            </small>
          </div>

          <button
            @click="chatOpen = false"
            class="btn btn-sm btn-outline-light"
          >
            ×
          </button>
        </div>

        <div
          ref="chatBody"
          class="p-3 chatbot-body"
        >
          <div
            v-for="(msg, index) in chatMessages"
            :key="index"
            class="mb-3"
            :class="msg.sender === 'user' ? 'text-end' : 'text-start'"
          >
            <div
              class="d-inline-block px-3 py-2 chatbot-bubble"
              :class="msg.sender === 'user' ? 'user-bubble' : 'bot-bubble'"
            >
              <span v-if="!msg.isHtml">{{ msg.text }}</span>
              <span v-else v-html="msg.text"></span>
            </div>

            <div
              v-if="msg.showWhatsapp"
              class="mt-2"
            >
              <button
                @click="openWhatsApp"
                class="btn btn-sm fw-bold"
                style="background:#2bbdee; color:white;"
              >
                Contact us on WhatsApp
              </button>
            </div>
          </div>
        </div>

        <div class="px-3 pb-2 d-flex flex-wrap gap-2">
          <button
            v-for="(s, i) in chatSuggestions"
            :key="i"
            @click="sendSuggestedQuestion(s)"
            class="btn btn-sm btn-outline-secondary rounded-pill"
          >
            {{ s }}
          </button>
        </div>

        <div class="p-3 border-top border-secondary">
          <form
            @submit.prevent="sendChatMessage"
            class="d-flex gap-2"
          >
            <input
              v-model="chatInput"
              type="text"
              class="form-control bg-dark text-light border-secondary"
              placeholder="Ask a question..."
            />

            <button
              type="submit"
              class="btn fw-bold"
              style="background:#2bbdee; color:white;"
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- FOOTER -->
    <footer class="bg-dark py-4 text-center border-top border-secondary">
      <p class="mb-0">
        © {{ currentYear }} AMD Transportation. All rights reserved.
      </p>

      <div class="d-flex justify-content-center align-items-center gap-3 mb-3 mt-3">
        <img
          src="@/assets/award-100.jpg"
          alt="Entrepreneur Award"
          style="height: 60px; width: auto; opacity: 0.9;"
          class="footer-award"
        />

        <img
          src="@/assets/award-banner.jpg"
          alt="Entrepreneur Award Banner"
          style="height: 60px; width: auto; opacity: 0.9;"
          class="footer-award"
        />
      </div>
    </footer>
  </div>
</template>

<script>
import heroImage from "@/assets/hero-transportation.jpg";
import {
  Handshake,
  FileBarChart,
  LocateFixed,
  Upload,
  BadgeDollarSign,
  Zap,
  CalendarCheck,
  Speech,
  BicepsFlexed,
  ShieldCheck,
  Ship,
  X,
  Check,
  MapPinCheckInside,
  Brush,
  MessageCircle,
  Clock,
} from "lucide-vue-next";
import confetti from "canvas-confetti";

export default {
  name: "AMDTransportationLanding",

  components: {
    Handshake,
    FileBarChart,
    LocateFixed,
    Upload,
    BadgeDollarSign,
    Zap,
    CalendarCheck,
    Speech,
    BicepsFlexed,
    ShieldCheck,
    Ship,
    X,
    Check,
    MapPinCheckInside,
    Brush,
    MessageCircle,
    Clock,
  },

  data() {
    return {
      showAward: true,
      activeFAQ: null,
      heroImage,

      chatOpen: false,
      chatInput: "",
      chatMessages: [
        {
          sender: "bot",
          text: "Hello! How can I help? You can ask about bookings, vehicle types, port access, ship delays, group transfers, or long-term contracts.",
          isHtml: false,
          showWhatsapp: false,
        },
      ],
      chatSuggestions: [
        "How do I book?",
        "Are you available 24/7?",
        "What vehicles do you provide?",
        "What if my ship is delayed?",
      ],

      problems: [
        "Flight delays causing missed pickups",
        "Unreliable drivers and poor communication",
        "Complex booking processes",
        "No real-time updates on driver location",
      ],

      solutions: [
        "Automatic flight tracking with instant updates",
        "Professional, vetted drivers with smart assignment",
        "One-click booking without account creation",
        "WhatsApp notifications and live tracking",
      ],

      features: [
        {
          icon: "Ship",
          title: "Crew Transfer Services",
          description:
            "Safe and timely transport of seafarers between <b>Ports, Yards, Piers, Hotels and Airport</b>\n24/7 availability to match irregular ship schedules.\nSupport for <b>group transfers</b> with mini-buses or maxi cabs",
        },
        {
          icon: "MapPinCheckInside",
          title: "Port-to-Port & Last-Mile Connectivity",
          description:
            "Efficient coordination with <b>port authorities</b> for smooth entry/exit\nLast-mile delivery of essential supplies, documents, or personnel",
        },
        {
          icon: "Brush",
          title: "Customizable Transport Solutions",
          description:
            "Hourly or fixed-rate bookings for flexibility\nTailored routes and schedules based on ship docking times and crew needs",
        },
        {
          icon: "MessageCircle",
          title: "Real-Time Tracking & Communication",
          description:
            "GPS-enabled vehicles for <b>live tracking</b>\nInstant updates and communication with dispatch teams",
        },
        {
          icon: "ShieldCheck",
          title: "Compliance & Safety Standards",
          description:
            "Adherence to <b>maritime safety protocols</b>\nTrained drivers familiar with port operations and security procedures",
        },
        {
          icon: "BicepsFlexed",
          title: "Luggage & Equipment Handling",
          description:
            "Support for transporting <b>crew luggage, tools, and ship supplies</b>\nVehicles equipped for <b>bulk or fragile cargo</b>",
        },
        {
          icon: "Speech",
          title: "Multilingual Support",
          description:
            "Drivers and coordinators often trained in <b>basic maritime English</b> and other languages to assist international crews",
        },
      ],

      benefits: [
        {
          icon: "Clock",
          title: "24/7 Accessibility",
          description:
            "Book transport anytime, anywhere—ideal for ship agents and crew managers working across time zones.",
        },
        {
          icon: "CalendarCheck",
          title: "Instant Confirmation",
          description:
            "Every driver is thoroughly background-checked, professionally trained, and committed to providing exceptional service. Your safety and comfort are guaranteed.",
        },
        {
          icon: "Zap",
          title: "Real-Time Scheduling",
          description:
            "View available vehicles, select preferred time slots, and adjust bookings based on ship arrival/departure changes",
        },
        {
          icon: "BadgeDollarSign",
          title: "Transparent Pricing",
          description:
            "Clear display of rates for point-to-point transfers, hourly bookings, and group transport—no hidden fees.",
        },
        {
          icon: "Brush",
          title: "Customizable Options",
          description:
            "Choose vehicle types (MPV, mini bus, coach), add special requests (e.g., luggage handling, multilingual driver), and specify pickup/drop-off points.",
        },
        {
          icon: "Upload",
          title: "Crew Manifest Upload",
          description:
            "Easily upload crew lists or documents to streamline coordination and reduce manual errors.",
        },
        {
          icon: "LocateFixed",
          title: "GPS Tracking & Driver Info",
          description:
            "Get driver details and live tracking links once the booking is confirmed—enhancing safety and accountability.",
        },
        {
          icon: "ShieldCheck",
          title: "Secure Payment Gateway",
          description:
            "Pay online via credit card or corporate account with encrypted transactions and invoicing support.",
        },
        {
          icon: "FileBarChart",
          title: "Booking History & Reports",
          description:
            "Access past bookings, download invoices, and generate transport reports for audits or planning.",
        },
        {
          icon: "Handshake",
          title: "Dedicated Support Integration",
          description:
            "Chat or call support directly from the booking portal for urgent changes or assistance.",
        },
      ],

      faqs: [
        {
          question: "1. What services do you offer for seafarers?",
          answer:
            "We provide reliable transport for seafarers between <b>ports, airports, hotels, and vessels. Our services include point-to-point transfers</b>, hourly bookings, and group transport using mini-buses and maxi cabs.",
        },
        {
          question: "2. Are your services available 24/7?",
          answer:
            "Yes, we operate <b>24 hours a day, 7 days a week</b> to accommodate ship schedules and last-minute crew changes.",
        },
        {
          question: "3. How do I book a transport service?",
          answer:
            "You can book via:<br/><b>Phone</b>: TBA<br/><b>Email</b>: amdtransportation18@gmail.com<br/><b>Website</b>: TBA",
        },
        {
          question: "4. What types of vehicles do you provide?",
          answer:
            "We offer a range of vehicles including:<br/><b>7-seater MPVs</b><br/><b>13-seater mini buses</b><br/><b>23- and 43-seater coaches</b><br/><br/>All vehicles are clean, air-conditioned, and equipped for luggage handling.",
        },
        {
          question: "5. Can you handle large groups or multiple crew members?",
          answer:
            "Absolutely. We specialize in <b>group transfers</b> and can coordinate multiple vehicles for large crews or staggered arrivals.",
        },
        {
          question: "6. Do your drivers have port access clearance?",
          answer:
            "Yes, our drivers are experienced and have the necessary <b>port access passes</b> and training to operate within Singapore's port areas.",
        },
        {
          question: "7. What if my ship is delayed?",
          answer:
            "We monitor ship schedules and stay in contact with agents. If there’s a delay, we adjust pickup times accordingly.",
        },
        {
          question: "8. Is there support for non-English speaking crew?",
          answer:
            "Yes, our team is trained to communicate clearly and respectfully with <b>multinational crews, and we provide basic multilingual support</b> when needed.",
        },
        {
          question: "9. Are your services safe and compliant with health protocols?",
          answer:
            "Yes, we follow <b>strict hygiene and safety standards</b>, including vehicle sanitization and driver health checks.",
        },
        {
          question: "10. Do you offer corporate or long-term contracts?",
          answer:
            "Yes, we provide <b>customized transport packages</b> for shipping companies, crew agencies, and maritime service providers.",
        },
      ],

      formData: {
        pickupAddress: "",
        dropoffAddress: "",
        date: "",
        time: "",
        name: "",
        phone: "",
        email: "",
        specialRequests: "",
      },

      currentYear: new Date().getFullYear(),
    };
  },

  mounted() {
    // this.launchConfetti();
  },

  methods: {
    launchConfetti() {
      confetti({
        particleCount: 180,
        spread: 100,
        startVelocity: 45,
        origin: { x: 0.5, y: 0.4 },
      });

      confetti({
        particleCount: 120,
        spread: 40,
        startVelocity: 35,
        origin: { x: 0.5, y: 0.4 },
      });
    },

    goLogin() {
      window.location.href = "/login";
    },

    toggleFAQ(index) {
      this.activeFAQ = this.activeFAQ === index ? null : index;
    },

    scrollToBooking() {
      const el = document.getElementById("booking-section");

      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    },

    openWhatsApp() {
      window.open(
        "https://wa.me/6590290017?text=Hello! I would like to book a limousine service.",
        "_blank"
      );
    },

    openChatbot() {
      this.chatOpen = true;
      this.scrollChatToBottom();
    },

    stripHtml(html) {
      const div = document.createElement("div");
      div.innerHTML = html || "";
      return div.textContent || div.innerText || "";
    },

    normalizeText(text) {
      return String(text || "")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    },

    getFaqScore(userQuestion, faq) {
      const query = this.normalizeText(userQuestion);
      const faqQuestion = this.normalizeText(faq.question);
      const faqAnswer = this.normalizeText(this.stripHtml(faq.answer));

      if (!query) return 0;

      let score = 0;

      if (faqQuestion.includes(query)) score += 10;
      if (query.includes(faqQuestion)) score += 8;

      const words = query
        .split(" ")
        .filter((word) => word.length >= 3);

      words.forEach((word) => {
        if (faqQuestion.includes(word)) score += 3;
        if (faqAnswer.includes(word)) score += 1;
      });

      const intentMap = {
        booking: ["book", "booking", "reserve", "transport"],
        vehicle: ["vehicle", "mpv", "bus", "coach", "seater", "car"],
        delay: ["delay", "late", "ship", "reschedule"],
        port: ["port", "access", "clearance", "pass"],
        contract: ["contract", "corporate", "long", "term", "package"],
        safety: ["safe", "safety", "hygiene", "sanitize", "sanitization"],
        group: ["group", "crew", "large", "multiple"],
        language: ["language", "english", "multilingual", "non", "speaking"],
      };

      Object.values(intentMap).forEach((keywords) => {
        const hasQueryKeyword = keywords.some((keyword) =>
          query.includes(keyword)
        );

        const hasFaqKeyword =
          keywords.some((keyword) => faqQuestion.includes(keyword)) ||
          keywords.some((keyword) => faqAnswer.includes(keyword));

        if (hasQueryKeyword && hasFaqKeyword) {
          score += 4;
        }
      });

      return score;
    },

    findBestFAQ(userQuestion) {
      let bestFAQ = null;
      let bestScore = 0;

      this.faqs.forEach((faq) => {
        const score = this.getFaqScore(userQuestion, faq);

        if (score > bestScore) {
          bestScore = score;
          bestFAQ = faq;
        }
      });

      return bestScore >= 4 ? bestFAQ : null;
    },

    sendChatMessage() {
      const message = this.chatInput.trim();

      if (!message) return;

      this.chatMessages.push({
        sender: "user",
        text: message,
        isHtml: false,
        showWhatsapp: false,
      });

      this.chatInput = "";

      const matchedFAQ = this.findBestFAQ(message);

      if (matchedFAQ) {
        this.chatMessages.push({
          sender: "bot",
          text: `
            <div class="fw-bold mb-1">${matchedFAQ.question}</div>
            <div>${matchedFAQ.answer}</div>
            <br/>
            <small class="text-muted">
              For exact booking confirmation, please contact our team directly.
            </small>
          `,
          isHtml: true,
          showWhatsapp: false,
        });
      } else {
        this.chatMessages.push({
          sender: "bot",
          text:
            "I’m not fully sure about that. Best to contact us directly so our team can confirm the details properly.",
          isHtml: false,
          showWhatsapp: true,
        });
      }

      this.scrollChatToBottom();
    },

    sendSuggestedQuestion(question) {
      this.chatInput = question;
      this.sendChatMessage();
    },

    scrollChatToBottom() {
      this.$nextTick(() => {
        if (this.$refs.chatBody) {
          this.$refs.chatBody.scrollTop = this.$refs.chatBody.scrollHeight;
        }
      });
    },
  },
};
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  max-height: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  max-height: 500px;
}

.btn-outline {
  color: #2bbdee;
  border-color: #2bbdee;
  transition: all 0.3s ease;
}

.btn-outline:hover {
  background-color: #2bbdee;
  color: white !important;
  border-color: #2bbdee;
}

.feature-desc {
  text-align: justify;
  text-align-last: left;
  hyphens: auto;
  overflow-wrap: anywhere;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0 auto;
  max-width: 90%;
  white-space: pre-line;
}

.feature-desc b,
.feature-desc strong {
  color: #fff;
  font-weight: 600;
}

.feature-title {
  min-height: 3em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  text-align: center;
}

.feature-card {
  min-height: 330px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.award-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: fadeIn 0.35s ease-out forwards;
}

.award-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(10px);
}

.award-popup {
  position: relative;
  z-index: 10;
  animation: scaleIn 0.4s ease-out forwards;
}

.chatbot-card {
  width: 360px;
  max-width: calc(100vw - 24px);
  border-radius: 18px;
  overflow: hidden;
}

.chatbot-header {
  background: #111;
}

.chatbot-body {
  height: 320px;
  overflow-y: auto;
}

.chatbot-bubble {
  max-width: 85%;
  word-break: break-word;
}

.user-bubble {
  background: #2bbdee;
  color: white;
  border-radius: 14px 14px 0 14px;
}

.bot-bubble {
  background: #222;
  color: white;
  border-radius: 14px 14px 14px 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.85);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 576px) {
  .amd-chatbot {
    left: 12px;
    right: 12px;
    margin: 0 0 12px 0 !important;
  }

  .chatbot-card {
    width: 100%;
  }

  .chatbot-body {
    height: 300px;
  }

  .display-3 {
    font-size: 2.4rem;
  }

  .footer-award {
    max-width: 45%;
    height: auto !important;
  }
}
</style>